import { GenericFilter } from './filters';
import { Issuer } from './issuers';
import { Market } from './markets';
import { Property } from './properties';
import { Tag } from './tag';

export type OfferingId = number;
export type OfferingShortName = string;
export type OfferingIdOrOfferingShortName = OfferingId | OfferingShortName;

export enum OfferingStatus {
  /**
   * Does not show up at all in application except in admin views.
   * No new reservations.
   * No new transactions.
   */
  PRE_LAUNCH = 'PRE_LAUNCH',

  /**
   * Coming soon previews.
   * Does not show full property details.
   */
  COMING_SOON = 'COMING_SOON',

  /**
   * Show full property details.
   * Allow new reservations.
   * No new transactions.
   */
  RESERVATIONS = 'RESERVATIONS',

  /**
   * Show full property details.
   * No new reservations.
   * No new transactions.
   * Allow funding of existing reservations.
   */
  TRANSACT_RESERVATIONS = 'TRANSACT_RESERVATIONS',

  /**
   * Show full property details.
   * Allow new transactions from early access users only, all other users see the Offering in a COMING SOON state.
   */
  TRANSACT_EARLY_ACCESS = 'TRANSACT_EARLY_ACCESS',

  /**
   * Show full property details.
   * Allow new transactions.
   * No new reservations.
   * Allow funding of existing reservations.
   */
  TRANSACT_ALL = 'TRANSACT_ALL',

  /**
   * Offering has completed funding.
   * View only.
   * No new reservations.
   * No new transactions.
   */
  FUNDED = 'FUNDED',

  /**
   * Show full property details.
   * View only.
   * No new reservations.
   * No new transactions.
   * Not a final state, simply to pause transactions and reservations.
   */
  PAUSED = 'PAUSED',
}

export enum ArrivedNCOfferingStatus {
  /**
   * Default status
   */
  PENDING = 'PENDING',

  /**
   * Offering has been approved for taking transactions.
   */
  APPROVED = 'APPROVED',

  /**
   * Offering has been rejected and can not take transactions.
   */
  NOT_APPROVED = 'NOT_APPROVED',
}

export enum OfferingTradeProcessingStatus {
  DEFAULT = 'DEFAULT',

  /**
   * Process all trades for this offering.
   * This will typically be the status of the "current batch" of offerings e.g. Drop 10 offerings.
   */
  ALL_TRADES = 'ALL_TRADES',
}

export enum OfferingType {
  EQUITY = 'EQUITY',
  DEBT = 'DEBT',
  HYBRID = 'HYBRID',
  FUND = 'FUND',
}

export enum SecurityType {
  REG_A = 'REG_A',
  REG_D = 'REG_D',
}

export enum AssetType {
  STR = 'STR',
  LTR = 'LTR',
}

export enum ValuationSource {
  HOUSE_CANARY = 'HOUSE_CANARY',
  RED_BELL = 'RED_BELL',
  ZILLOW = 'ZILLOW',
}

export interface OfferingPropertyValuationCreateRequest {
  offeringId: OfferingId;
  propertyId: number;
  source: ValuationSource;
  amount?: number;
  minAmount?: number;
  maxAmount?: number;
  date: Date;
  documentUrl?: string;
}

export interface OfferingPropertyValuation extends OfferingPropertyValuationCreateRequest {
  id: number;
}

export interface OfferingPropertyRequest {
  id: number;
  propertyIds: number[];
}

/**
 * An OfferingIdOrShortName can either be the actual numerical id of the offering, or the short name.
 */
export type OfferingIdOrShortName = OfferingId | Offering['shortName'];

export interface OfferingDividend {
  id: number;
  offeringId: OfferingId;
  dividendPerShare: number;
  annualizedDividendPercent: number;
  startDate: Date;
  endDate: Date;
  postedAt: string;
}

export interface Offering {
  id: OfferingId;
  issuer: Issuer;
  status: OfferingStatus;
  ncStatus: ArrivedNCOfferingStatus;
  tradeProcessingStatus: OfferingTradeProcessingStatus;
  isFund: boolean;
  securityType: SecurityType;
  assetType: AssetType;
  market?: Market;
  type: OfferingType;
  name: string;
  shortName: OfferingShortName;
  llcName?: string;
  description: string;
  stampingText: string;
  targetRaiseAmount: number;
  maxRaiseAmount: number;
  minTransactionAmount: number;
  sharePrice: number;
  totalShares: number;
  totalSharesTransacted: number;
  totalSharesReserved: number;
  lastTransactionDate: string | null;
  maxSharesPerAccount: number | null;
  investorsCount: number;
  reservedInvestorsCount: number;
  waitlistCount: number;
  reviewsCount: number;
  dropId: number | null;
  tags: Tag[];
  hasFlexFinancing: boolean;

  estimatedEarningsAnnual: number;
  estimatedAppreciationAnnual: number;
  estimatedLeveragedAppreciationAnnual: number; // read only value
  estimatedReturnAnnual: number;

  debtPercent: number;
  debtAmount: number;
  debtInterestPercent: number;
  holdPeriodYearsMin: number;
  holdPeriodYearsMax: number;
  arrivedOneTimeProceeds: number;
  startAt: Date;
  endAt: Date;

  closingOfferingAndHoldingCosts: number;
  propertyImprovementsAndCashReserves: number;
  quarterlyAumFeeAmount: number | null;
  // TODO remove dividend calculator specific fields
  vacancyRatePercent: number; // new, used in dividend calculator. should be property model
  maintenancePercent: number; // new, used in dividend calculator. should be property model
  managementFeeAmount: number; // old
  insuranceAmount: number; // new, used in dividend calculator. not yet part of backend offering model, should be property model?
  propertyTaxPercent: number; // new, used in dividend calculator. should be property model
  leasingIncentivePercent: number; // new, used in dividend calculator. should be property model or constant?
  propertyManagementPercent: number; // new, used in dividend calculator. should be property model or constant?
  rentalRevenueAmount: number;
  projectedFirstDividendDate: string | null;
  projectedFirstDividendAmount: string | null;
  projectedAnnualDividendYield: number | null;

  projectedAppreciationLower: number | null;
  projectedAppreciationUpper: number | null;

  // annual operating expenses
  operatingExpensesAmount: number;

  // annual noi
  netCashFlowAmount: number; // annual noi, old name "netCashFlowAmount"

  latestValuation: OfferingPropertyValuation | null;

  // dividends
  latestDividend: OfferingDividend | null;

  // derived values
  totalPurchasePrice: number;
  /**
   * The % of the Offering that has been funded including debt.
   */
  fundedPercent: number;

  /**
   * The % of the shares in the Offering that have been purchased.
   */
  fundedPercentNoDebt: number;

  // joined models
  properties: Property[];
}

export interface OfferingTradesFilters extends GenericFilter {
  offeringId?: OfferingId;
}
